import type { RawEditorOptions } from "tinymce"
import * as FlatModels from '@medsurf/flat-models'
import wordFilter from 'tinymce-word-paste-filter';
import { iconClasses } from '@medsurf/flat-css'


export type EditorConfigOptions = {
    minHeight?: number
    height?: number | string
    placeholder?: string
    autogrow?: boolean
    toolbar?: string[][]
    toolbarExcludes?: string[]
}

const defaultPlugins = [
    'fullscreen',
    'lists',
    'code',
    'charmap',
    'searchreplace',
    'table',
    'help',
    'wordcount',
    'accordion',
    'emoticons'
]

const defaultToolbar = [
  ['blocks', 'styles'],
  [
    'bold',
    'italic',
    'underline',
    'forecolor',
  ],
  [
    'subscript',
    'superscript',
    'strikethrough'
  ],
  [
    'link',
    'marker',
    'unlink'
  ],
  [
    'mediaimage',
    'mediapdf',
    'mediaaudio',
    'mediavideo'
  ],
  [
    'bullist',
    'numlist',
    'outdent',
    'indent'
  ],
  [
    'table',
    'accordion'
  ],
  ['charmap', 'emoticons'],
  ['removeformat'],
  [
    'code',
    'fullscreen',
    'example',
    'help'
  ]
];

const tableBackgrounds = {
  title: 'Background color', 
  menu: [
    {title: 'Success', value: 'mds-table-success'},
    {title: 'Danger', value: 'mds-table-danger'},
    {title: 'Warning', value: 'mds-table-warning'},
    {title: 'Info', value: 'mds-table-info'},
    {title: 'Light', value: 'mds-table-light'},
    {title: 'Dark', value: 'mds-table-dark'},
  ]
};

const allowedClasses = [
    'mds-internal', 'mds-external', 'mds-marker',
    'mds-box-format', 'mds-attention', 'mds-note', 'mds-no-outline', 'mds-only-background',
    'mds-only-outline', 'mds-plus-marker', 'mds-box-button',
    'mds-align-center', 'mds-align-right',
    'mds-w-100', 'mds-w-90', 'mds-w-80', 'mds-w-70', 'mds-w-60', 'mds-w-50', 
    'mds-w-40', 'mds-w-33', 'mds-w-30', 'mds-w-25', 'mds-w-20', 'mds-w-10',
    'mds-table', 'mds-table-success', 'mds-table-danger', 'mds-table-warning',
    'mds-table-info', 'mds-table-light', 'mds-table-dark', 'mds-table-striped',
    'mds-table-primary', 'mds-table-secondary',
    'mds-table-bordered', 'mds-table-hover', 'mds-table-sm',
    'mds-important-primary', 'mds-important-secondary', 'mds-important-tertiary', 'mds-important-quaternary', 
    'mds-interaction-regular', 'mds-interaction-strong',
    'mds-list-wide-spacing', 'mds-list-unstyled',
    'mds-btn', 'mds-btn-outline',
    'mds-p-0', 'mds-p-1', 'mds-p-2', 'mds-p-3', 'mds-p-4', 'mds-p-5',
    'mds-pt-0', 'mds-pt-1', 'mds-pt-2', 'mds-pt-3', 'mds-pt-4', 'mds-pt-5',
    'mds-pb-0', 'mds-pb-1', 'mds-pb-2', 'mds-pb-3', 'mds-pb-4', 'mds-pb-5',
    'mds-pl-0', 'mds-pl-1', 'mds-pl-2', 'mds-pl-3', 'mds-pl-4', 'mds-pl-5',
    'mds-pr-0', 'mds-pr-1', 'mds-pr-2', 'mds-pr-3', 'mds-pr-4', 'mds-pr-5',
    'mds-px-0', 'mds-px-1', 'mds-px-2', 'mds-px-3', 'mds-px-4', 'mds-px-5',
    'mds-py-0', 'mds-py-1', 'mds-py-2', 'mds-py-3', 'mds-py-4', 'mds-py-5',
    'mds-m-0', 'mds-m-1', 'mds-m-2', 'mds-m-3', 'mds-m-4', 'mds-m-5',
    'mds-mt-0', 'mds-mt-1', 'mds-mt-2', 'mds-mt-3', 'mds-mt-4', 'mds-mt-5',
    'mds-mb-0', 'mds-mb-1', 'mds-mb-2', 'mds-mb-3', 'mds-mb-4', 'mds-mb-5',
    'mds-ml-0', 'mds-ml-1', 'mds-ml-2', 'mds-ml-3', 'mds-ml-4', 'mds-ml-5',
    'mds-mr-0', 'mds-mr-1', 'mds-mr-2', 'mds-mr-3', 'mds-mr-4', 'mds-mr-5',
    'mds-mx-0', 'mds-mx-1', 'mds-mx-2', 'mds-mx-3', 'mds-mx-4', 'mds-mx-5',
    'mds-mx-auto',
    'mds-my-0', 'mds-my-1', 'mds-my-2', 'mds-my-3', 'mds-my-4', 'mds-my-5',
    'mds-h1', 'mds-h2', 'mds-h3', 'mds-h4', 'mds-h5', 'mds-h6',
    'mds-text-left', 'mds-text-center', 'mds-text-right',
    'mds-text-decoration-none',
    'mds-absolute', 'mds-relative',
    'mds-float-left', 'mds-float-right',
    'mds-bg-danger', 'mds-bg-info', 'mds-bg-success', 'mds-bg-warning',
    'mds-border',
    'mds-block', 'mds-inline-block', 'mds-none', 'mds-lg-block', 'mds-lg-none', 'mds-md-block', 'mds-md-none', 'mds-sm-block', 'mds-sm-none',
    'mce-accordion'
]

export const getEditorConfig = (options: EditorConfigOptions): RawEditorOptions => {

    const plugins = [...defaultPlugins]
    if (options.autogrow) plugins.push('autoresize');

    const rawToolbar = options.toolbar ?? defaultToolbar;
    const toolbarExcludeSet = new Set(options.toolbarExcludes ?? []);
    const toolbar = rawToolbar.map((group) => {
        const filteredGroup = group.filter((item) => !toolbarExcludeSet.has(item))
        return filteredGroup.join(' ')
    }).join(' | ');

    return {
        // Base
        base_url: '/tinymce',
        suffix: '.min',
        min_height: options.minHeight,
        height: options.height,
        menubar: false,
        entity_encoding: 'raw',
        toolbar_mode: 'sliding',
        invalid_styles: {
            '*': 'font-family',
            'td': 'width height',
            'th': 'width height',
            'tr': 'width height',
        },
        language: 'de',
        convert_urls: false,
        language_url: '/assets/tinymce/langs/de.js',
        branding: true,
        browser_spellcheck: true,
        placeholder: options.placeholder,
        keep_styles: false,
        end_container_on_empty_block: true,
        paste_data_images: false,
        valid_classes: [...allowedClasses, ...iconClasses].join(' '),
        // Plugins
        plugins,
        // Toolbar
        toolbar,
        // Table
        table_default_attributes: {class: 'mds-table'},
        table_resize_bars: false,
        table_sizing_mode: 'relative',
        table_class_list: [
            {title: 'None', value: 'mds-table '},
            {title: 'Zebra striped', value: 'mds-table mds-table-striped'},
            {title: 'Bordered', value: 'mds-table mds-table-bordered'},
            {title: 'Hover', value: 'mds-table mds-table-hover'},
            {title: 'Small', value: 'mds-table mds-table-sm'}
        ],
        table_row_class_list: [
            {title: 'None', value: ''},
            tableBackgrounds,
        ],
        table_cell_class_list: [
            {title: 'None', value: ''},
            tableBackgrounds,
        ],
        // Formats
        style_formats: [
            { 
                title: 'Auszeichnungen', 
                items: [
                    { title: 'Hervorhebung', format: 'important-primary' },
                    { title: 'Direkte Rede', format: 'important-secondary' },
                    { title: 'Wichtig',format: 'important-tertiary' },
                    { title: 'Literatur', format: 'important-quaternary' }
                ]
            },
            { 
                title: 'Interaktionen', 
                items: [
                    { title: 'Normale Interaktion', format: 'interaction-regular' },
                    { title: 'Wichtige Interaction', format: 'interaction-strong' },
                ]
            },
            { 
                title: 'Listen',
                items: [
                    { title: 'Grosse Abstände', format: 'wide-spacing' },
                ]
            }
        ],
        block_formats: 'Paragraph=p; Header 1=h1; Header 2=h2; Header 3=h3; Header 4=h4; Header 5=h5; Header 6=h6; Box=box-format;',
        formats: {
            'box-format': {
                block: 'div',
                classes: 'mds-box-format',
                wrapper: true
            },
            'important-primary': {
                inline: 'span',
                attributes: { class: 'mds-important-primary' },
            },
            'important-secondary': {
                inline: 'span',
                attributes: { class: 'mds-important-secondary' },
            },
            'important-tertiary': {
                inline: 'span',
                attributes: { class: 'mds-important-tertiary' },
            },
            'important-quaternary': {
                inline: 'span',
                attributes: { class: 'mds-important-quaternary' },
            },
            'interaction-regular': {
                inline: 'span',
                attributes: { class: 'mds-interaction-regular' },
            },
            'interaction-strong': {
                inline: 'span',
                attributes: { class: 'mds-interaction-strong' },
            },
            'wide-spacing': {
                selector: 'ul, ol',
                classes: 'mds-wide-spacing',
            }
        },
        style_formats_merge: false,
        // Extra Style
        content_css: "/assets/styles/format.css",
        content_style: `
            @font-face {
                font-family: 'Figtree';
                font-style: italic;
                font-weight: 300 700;
                font-display: swap;
                src: url(/assets/fonts/_Xmu-HUzqDCFdgfMm4GNAa5o_ik.woff2) format('woff2');
                unicode-range: U+0100-024F, U+0259, U+1E00-1EFF, U+2020, U+20A0-20AB, U+20AD-20CF, U+2113, U+2C60-2C7F, U+A720-A7FF;
            }
            @font-face {
                font-family: 'Figtree';
                font-style: italic;
                font-weight: 300 700;
                font-display: swap;
                src: url(/assets/fonts/_Xmu-HUzqDCFdgfMm4GND65o.woff2) format('woff2');
                unicode-range: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD;
            }
            @font-face {
                font-family: 'Figtree';
                font-style: normal;
                font-weight: 300 700;
                font-display: swap;
                src: url(/assets/fonts/_Xms-HUzqDCFdgfMm4q9DbZs.woff2) format('woff2');
                unicode-range: U+0100-024F, U+0259, U+1E00-1EFF, U+2020, U+20A0-20AB, U+20AD-20CF, U+2113, U+2C60-2C7F, U+A720-A7FF;
            }
            @font-face {
                font-family: 'Figtree';
                font-style: normal;
                font-weight: 300 700;
                font-display: swap;
                src: url(/assets/fonts/_Xms-HUzqDCFdgfMm4S9DQ.woff2) format('woff2');
                unicode-range: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD;
            }`,
        skin_url: FlatModels.SlideAuthorModels.MedsurfThemeEditorStyleFile[FlatModels.SlideAuthorModels.MedsurfTheme.enum.dark],
        // Hooks
        paste_preprocess: (_, args) => {
            const c = document.createElement('div');
            c.innerHTML = args.content;
            
            const elems = c.querySelectorAll('*');
            elems.forEach((el) => {
                const attributes = Array.from(el.attributes);
                attributes.forEach((attr) => {
                if (attr.name.startsWith('data-') || attr.name.startsWith('aria-')) {
                    el.removeAttribute(attr.name);
                }
                })
            })
            
            args.content = c.innerHTML;
            args.content = wordFilter(args.content)
        },
    }
}
